'use client';

import React, { useEffect } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { toast } from 'react-hot-toast';
import { NotificationCenter } from './NotificationCenter';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { listAlerts, markAllAlertsRead } from '../lib/securityApi';

interface Notification {
  id: string;
  type: 'fileUpload' | 'fileDelete' | 'fileShare' | 'storageWarning' | 'error';
  message: string;
  timestamp: Date;
}

const STORAGE_KEY = 'notifications';
const MAX_NOTIFICATIONS = 50;

function alertToNotification(alert: {
  id: string;
  title: string;
  body?: string;
  createdAt: string;
  externalId?: string;
}): Notification {
  // The alert bus speaks suite-wide kinds; the drive bell speaks drive events.
  const type: Notification['type'] = alert.externalId?.includes('storage-warning')
    ? 'storageWarning'
    : alert.externalId?.includes('share')
      ? 'fileShare'
      : 'fileUpload';
  return {
    id: alert.id,
    type,
    message: alert.body ? `${alert.title} — ${alert.body}` : alert.title,
    timestamp: new Date(alert.createdAt),
  };
}

export const NotificationListener: React.FC = () => {
  const { socket, connected } = useWebSocket();
  const [notifications, setNotifications] = useLocalStorage<Notification[]>(STORAGE_KEY, []);

  const addNotification = (notification: Notification) => {
    setNotifications(prev => {
      if (prev.some((entry) => entry.id === notification.id)) return prev;
      const updated = [notification, ...prev].slice(0, MAX_NOTIFICATIONS);
      // Broadcast to other tabs
      broadcastNotificationUpdate(updated);
      return updated;
    });
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    broadcastNotificationUpdate([]);
    // The durable copy lives in the suite's alert pool — clear it there too.
    void markAllAlertsRead().catch(() => undefined);
  };

  // Server-side persistence: the alerts pool is what survives a cleared browser,
  // so the local list is seeded from it (drive events only) on mount.
  useEffect(() => {
    void listAlerts(50)
      .then((alerts) => {
        if (!alerts.length) return;
        setNotifications((prev) => {
          const existing = new Set(prev.map((entry) => entry.id));
          const merged = [
            ...alerts.filter((alert) => !existing.has(alert.id)).map(alertToNotification),
            ...prev,
          ].slice(0, MAX_NOTIFICATIONS);
          broadcastNotificationUpdate(merged);
          return merged;
        });
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-tab synchronization
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const newNotifications = JSON.parse(e.newValue);
        setNotifications(newNotifications);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // The suite topbar's bell badge mirrors the stored list.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('sak:notifications-updated', { detail: { count: notifications.length } }),
    );
  }, [notifications]);

  const broadcastNotificationUpdate = (updatedNotifications: Notification[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedNotifications));
  };

  useEffect(() => {
    if (!socket || !connected) return;

    // The hub's vault emits to this user's personal room: `fileUploaded` (the file row),
    // `fileDeleted` ({ id }), `storageWarning` ({ used, quota }). The payload IS the data —
    // there is no `{ payload }` envelope like the sibling's old service had.
    const handlers = {
      fileUploaded: (file: any) => {
        const notification: Notification = {
          id: crypto.randomUUID(),
          type: 'fileUpload' as const,
          message: `"${file?.name ?? 'A file'}" uploaded successfully`,
          timestamp: new Date(),
        };
        addNotification(notification);
        toast.success(notification.message);
      },

      fileDeleted: (data: any) => {
        const notification: Notification = {
          id: crypto.randomUUID(),
          type: 'fileDelete' as const,
          message: 'A file was moved to trash',
          timestamp: new Date(),
        };
        void data;
        addNotification(notification);
        toast.success(notification.message);
      },

      shareAccessed: (data: any) => {
        const notification: Notification = {
          id: crypto.randomUUID(),
          type: 'fileShare' as const,
          message: `"${data?.file?.name ?? 'A shared file'}" was downloaded from your link`,
          timestamp: new Date(),
        };
        addNotification(notification);
        toast.success(notification.message);
      },

      storageWarning: (data: any) => {
        const pct = data?.quota ? Math.round((Number(data.used) / Number(data.quota)) * 100) : 90;
        const notification: Notification = {
          id: crypto.randomUUID(),
          type: 'storageWarning' as const,
          message: `Storage is ${pct}% full`,
          timestamp: new Date(),
        };
        addNotification(notification);
        toast.error(notification.message); // toast.warning doesn't exist
      },

      // The hub's alert bus — durable, drive-filtered events (share downloads,
      // storage warnings, files shared with you) arriving through the same room.
      'alert:new': (data: any) => {
        const alert = data?.alert;
        if (!alert?.externalId?.startsWith?.('sak:')) return;
        const notification = alertToNotification(alert);
        addNotification(notification);
        toast.success(notification.message);
      },
    };

    // Register event listeners
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    // Cleanup: remove these exact handlers, not every listener on the event —
    // other components listen to the same socket events too.
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
    };
  }, [socket, connected]);

  // The bell button lives in the suite topbar; only the panel is mounted here,
  // opened from the topbar via sak:open-notifications.
  return (
    <NotificationCenter
      notifications={notifications}
      onClearAll={clearAllNotifications}
    />
  );
};